import { useState, useEffect } from "react";
import { estimateQuote, fetchServices } from "@/lib/api";
import { toast } from "sonner";
import { Calculator, Lightning, CheckCircle } from "@phosphor-icons/react";

export default function QuoteCalculator() {
    const [services, setServices] = useState([]);
    const [form, setForm] = useState({ service_type: "", acreage: 5, complexity: "standard", rush: false });
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchServices()
            .then((s) => {
                setServices(s);
                if (s.length) setForm((f) => ({ ...f, service_type: s[0].slug }));
            })
            .catch(() => {});
    }, []);

    const calc = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const r = await estimateQuote({
                service_type: form.service_type,
                acreage: parseFloat(form.acreage) || 0,
                complexity: form.complexity,
                rush: form.rush,
            });
            setResult(r);
        } catch {
            toast.error("Could not calculate estimate. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const fmt = (n) =>
        new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

    return (
        <div className="tech-card p-6 md:p-8" data-testid="quote-calculator">
            <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-cyan-500/10 border border-cyan-500/30">
                    <Calculator size={20} weight="duotone" className="text-cyan-300" />
                </div>
                <div>
                    <div className="overline">Instant estimator</div>
                    <h3 className="font-heading text-xl font-semibold text-slate-50">Quick Quote Calculator</h3>
                </div>
            </div>

            <form onSubmit={calc} className="space-y-4">
                <div>
                    <label className="overline mb-2 block">Service Type</label>
                    <select
                        value={form.service_type}
                        onChange={(e) => setForm({ ...form, service_type: e.target.value })}
                        className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500"
                        data-testid="calc-service-select"
                    >
                        {services.map((s) => (
                            <option key={s.slug} value={s.slug}>
                                {s.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="overline mb-2 block">
                        Acreage <span className="text-cyan-400">({form.acreage} ac)</span>
                    </label>
                    <input
                        type="range"
                        min="1"
                        max="500"
                        step="1"
                        value={form.acreage}
                        onChange={(e) => setForm({ ...form, acreage: e.target.value })}
                        className="w-full accent-cyan-500"
                        data-testid="calc-acreage-input"
                    />
                    <div className="flex justify-between text-[10px] text-slate-500 font-mono-alt mt-1">
                        <span>1 ac</span><span>250 ac</span><span>500 ac</span>
                    </div>
                </div>
                <div>
                    <label className="overline mb-2 block">Complexity</label>
                    <div className="grid grid-cols-3 gap-2">
                        {["simple", "standard", "complex"].map((c) => (
                            <button
                                key={c}
                                type="button"
                                onClick={() => setForm({ ...form, complexity: c })}
                                className={`py-2 rounded-lg text-xs font-heading font-medium capitalize transition-all ${
                                    form.complexity === c
                                        ? "bg-cyan-500/20 border border-cyan-400/60 text-cyan-200"
                                        : "bg-slate-900/60 border border-slate-800 text-slate-400 hover:text-slate-200"
                                }`}
                                data-testid={`calc-complexity-${c}`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </div>
                <label className="flex items-center gap-2.5 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={form.rush}
                        onChange={(e) => setForm({ ...form, rush: e.target.checked })}
                        className="w-4 h-4 accent-orange-500"
                        data-testid="calc-rush-checkbox"
                    />
                    <span className="text-sm text-slate-300 flex items-center gap-1.5">
                        <Lightning size={14} weight="fill" className="text-orange-400" /> Rush delivery (+40%)
                    </span>
                </label>
                <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full justify-center"
                    data-testid="calc-estimate-btn"
                >
                    {loading ? "Calculating…" : "Get Instant Estimate"}
                </button>
            </form>

            {result && (
                <div
                    className="mt-6 p-5 rounded-xl border border-cyan-500/40 bg-gradient-to-br from-cyan-500/10 to-blue-500/5"
                    data-testid="calc-result"
                >
                    <div className="flex items-center gap-2 mb-2 text-cyan-300">
                        <CheckCircle size={18} weight="fill" />
                        <span className="overline">Estimated range</span>
                    </div>
                    <div className="text-3xl font-heading font-bold text-slate-50">
                        {fmt(result.estimated_low)} — {fmt(result.estimated_high)}
                    </div>
                    <div className="mt-2 text-sm text-slate-400">
                        Turnaround: <span className="text-cyan-400 font-mono-alt">{result.turnaround_days} business days</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-3 leading-relaxed">
                        Indicative pricing for planning. Final quote depends on deliverables, source data quality, and
                        state-specific requirements.
                    </p>
                </div>
            )}
        </div>
    );
}
